import { useMemo, useState } from 'react'
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import TextFieldsIcon from '@mui/icons-material/TextFields'
import { FOOTER_TYPES, HEADER_TYPES } from 'src/consumer/constants'

const PAGE_NUMBERS = [1, 2, 3]

function getActivePages(kind, optionId) {
  if (kind === 'header') {
    if (optionId === HEADER_TYPES.onFirstPage) {
      return [1]
    }
    if (optionId === HEADER_TYPES.onAlternatePages) {
      return [1, 3]
    }
    return PAGE_NUMBERS
  }

  if (optionId === FOOTER_TYPES.onLastPage || optionId === FOOTER_TYPES.inLineAtEnd) {
    return [3]
  }
  if (optionId === FOOTER_TYPES.onAlternatePages) {
    return [1, 3]
  }
  return PAGE_NUMBERS
}

function PagePreview({ kind, option }) {
  const activePages = getActivePages(kind, option?.id)
  const isInlineFooter = kind === 'footer' && option?.id === FOOTER_TYPES.inLineAtEnd

  return (
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ my: 2 }}>
      {PAGE_NUMBERS.map((pageNumber) => {
        const isActive = activePages.includes(pageNumber)

        return (
          <Box
            key={pageNumber}
            sx={{
              width: { xs: '100%', sm: 124 },
              height: 172,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              bgcolor: 'background.paper',
              p: 1,
              display: 'flex',
              flexDirection: 'column',
              gap: 0.75,
            }}
          >
            <Typography variant="caption" color="text.secondary">
              Page {pageNumber}
            </Typography>
            {kind === 'header' && isActive && (
              <Box
                sx={{
                  height: 22,
                  borderRadius: 0.75,
                  bgcolor: 'primary.light',
                  color: 'primary.contrastText',
                  fontSize: 10,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                Header
              </Box>
            )}
            <Stack spacing={0.5} sx={{ flex: 1, justifyContent: 'center' }}>
              {[0, 1, 2, 3].map((line) => (
                <Box
                  key={line}
                  sx={{
                    height: 6,
                    borderRadius: 1,
                    bgcolor: line === 0 ? 'grey.300' : 'grey.200',
                    width: `${88 - line * 10}%`,
                  }}
                />
              ))}
              {isInlineFooter && pageNumber === 3 && (
                <Box
                  sx={{
                    mt: 0.75,
                    height: 24,
                    borderRadius: 0.75,
                    bgcolor: 'secondary.light',
                    color: 'secondary.contrastText',
                    fontSize: 10,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  Inline footer
                </Box>
              )}
            </Stack>
            {kind === 'footer' && isActive && !isInlineFooter && (
              <Box
                sx={{
                  height: 22,
                  borderRadius: 0.75,
                  bgcolor: 'primary.light',
                  color: 'primary.contrastText',
                  fontSize: 10,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                Footer
              </Box>
            )}
          </Box>
        )
      })}
    </Stack>
  )
}

export default function TemplatePlacementHelpDialog({ label, kind, options = [] }) {
  const [open, setOpen] = useState(false)
  const [selectedId, setSelectedId] = useState(options?.[0]?.id)

  const selectedOption = useMemo(
    () => options.find((option) => option?.id === selectedId) || options?.[0],
    [options, selectedId],
  )

  return (
    <>
      <Tooltip title={`View ${label?.toLowerCase()} examples`}>
        <Button
          size="small"
          variant="text"
          startIcon={<InfoOutlinedIcon fontSize="small" />}
          onClick={() => setOpen(true)}
          sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
        >
          View examples
        </Button>
      </Tooltip>
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="md">
        <DialogTitle sx={{ pr: 6 }}>
          {label} examples
          <IconButton
            aria-label="Close"
            onClick={() => setOpen(false)}
            size="small"
            sx={{ position: 'absolute', right: 16, top: 14 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2.5}>
            <Stack spacing={1} sx={{ minWidth: { md: 220 } }}>
              {options.map((option) => (
                <Button
                  key={option?.id}
                  variant={option?.id === selectedOption?.id ? 'contained' : 'outlined'}
                  color="primary"
                  onClick={() => setSelectedId(option?.id)}
                  sx={{
                    justifyContent: 'flex-start',
                    textTransform: 'none',
                    py: 1,
                    borderRadius: 1,
                  }}
                >
                  {option?.title}
                </Button>
              ))}
            </Stack>

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="subtitle2" fontWeight={600}>
                {selectedOption?.title}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {selectedOption?.description}
              </Typography>

              <PagePreview kind={kind} option={selectedOption} />

              <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 1.5 }}>
                <Chip
                  size="small"
                  icon={<ImageOutlinedIcon />}
                  label={selectedOption?.imageSupportText}
                  sx={{
                    height: 'auto',
                    maxWidth: '100%',
                    '& .MuiChip-label': {
                      whiteSpace: 'normal',
                      py: 0.5,
                    },
                  }}
                />
                <Chip
                  size="small"
                  icon={<TextFieldsIcon />}
                  label={`Works with built-in ${kind} content.`}
                  sx={{
                    height: 'auto',
                    maxWidth: '100%',
                    '& .MuiChip-label': {
                      whiteSpace: 'normal',
                      py: 0.5,
                    },
                  }}
                />
              </Stack>
              <Typography variant="body2">{selectedOption?.example}</Typography>
            </Box>
          </Stack>
        </DialogContent>
      </Dialog>
    </>
  )
}
